import { useCallback, useRef } from 'react';

import { getDwellTimeHelper } from '@confluence/analytics-web-client';

import { useSendAnalyticsEvents } from '../analytics/useSendAnalyticsEvents.tsx';
import type { DwellTimeHelperData, DwellTimeHelperType } from '../analytics/DwellTimeHelper.ts';

import { loadSpaceAndContentData } from './getConsumptionEventData.ts';
import {
	createConsumptionErrorEvent,
	createConsumptionCompletedEvent,
	createConsumptionInitiatedEvent,
	createConsumptionViewedEvent,
} from './ConsumptionEventMapping.ts';
import type {
	ConsumptionContentAttributes,
	ConsumptionEventRequiredData,
	ConsumptionInitiatedEventData,
	ConsumptionViewedEvent,
} from './ConsumptionEvents.ts';

export const useConsumptionEvents = () => {
	const { sendEvent } = useSendAnalyticsEvents();
	const dwellTimeHelperRef = useRef<DwellTimeHelperType | null>(null);

	const sendErrorEvent = useCallback(
		(errorMessage: string, data: ConsumptionEventRequiredData) => {
			const unifiedErrorEvent = createConsumptionErrorEvent(errorMessage, data);
			sendEvent(unifiedErrorEvent);
		},
		[sendEvent],
	);

	const sendInitiatedEvent = useCallback(
		(
			data: ConsumptionInitiatedEventData,
			contentAttributesOverride?: Partial<ConsumptionContentAttributes>,
		) => {
			loadSpaceAndContentData(data.spaceKey, data.contentId)
				.then((response) => {
					const event = createConsumptionInitiatedEvent(response, data, contentAttributesOverride);
					sendEvent(event);
				})
				.catch((error: Error) => sendErrorEvent(error.message, data));
		},
		[sendErrorEvent, sendEvent],
	);

	const sendViewedEvent = useCallback(
		(
			data: ConsumptionEventRequiredData,
			contentAttributesOverride?: Partial<ConsumptionContentAttributes>,
		) => {
			loadSpaceAndContentData(data.spaceKey, data.contentId)
				.then((response) => {
					const event = createConsumptionViewedEvent(response, data, contentAttributesOverride);
					sendEvent(event);
				})
				.catch((error: Error) => sendErrorEvent(error.message, data));
		},
		[sendErrorEvent, sendEvent],
	);

	const sendCompletedEvent = useCallback(
		(event: ConsumptionViewedEvent, dwellTimeHelperData: DwellTimeHelperData) => {
			if (dwellTimeHelperData.attributes.finalDwellEvent) {
				const consumptionCompletedEvent = createConsumptionCompletedEvent(
					event,
					dwellTimeHelperData,
					// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
					document.visibilityState,
				);
				sendEvent(consumptionCompletedEvent, true);
			}
		},
		[sendEvent],
	);

	const sendViewedEventAndCompletedEvent = useCallback(
		(
			data: ConsumptionEventRequiredData,
			contentAttributesOverride?: Partial<ConsumptionContentAttributes>,
		) => {
			const sendEventPromise = loadSpaceAndContentData(data.spaceKey, data.contentId).then(
				(response) => {
					const event = createConsumptionViewedEvent(response, data, contentAttributesOverride);
					sendEvent(event);
					return event;
				},
			);

			Promise.all([sendEventPromise, getDwellTimeHelper() as Promise<typeof DwellTimeHelperType>])
				.then(([event, DwellTimeHelper]) => {
					const dwellTimeHelper = new DwellTimeHelper(
						(callbackData) => sendCompletedEvent(event, callbackData),
						event.data,
					);
					dwellTimeHelper.start();

					dwellTimeHelperRef.current = dwellTimeHelper;
				})
				.catch((error: Error) => {
					sendErrorEvent(error.message, data);
				});
		},
		[sendEvent, sendCompletedEvent, sendErrorEvent],
	);

	const stopDwellTimeTracker = useCallback(() => {
		if (dwellTimeHelperRef.current) {
			dwellTimeHelperRef.current.stop();
		}
	}, []);

	return {
		sendInitiatedEvent,
		sendViewedEvent,
		sendViewedEventAndCompletedEvent,
		sendCompletedEvent,
		sendErrorEvent,
		stopDwellTimeTracker,
	};
};
